<script setup lang="ts">
	import { computed } from 'vue'

	import HasPermission from '@/components/HasPermission.vue'
	import SlideOver from '@/components/SlideOver.vue'
	import StyledSelect from '@/components/StyledSelect.vue'

	import { Role } from '@/modules/roles/composables/useRoles'

	interface Props {
		filters: any
		roles: Role[]
		open?: boolean
	}

	interface Emits {
		(e: 'close'): void
		(e: 'reset'): void
		(e: 'update:status', value: string): void
		(e: 'update:role', value: string): void
		(e: 'update:sortBy', value: string): void
		(e: 'update:sort', value: string): void
	}

	const props = withDefaults(defineProps<Props>(), {
		open: false,
	})

	const emits = defineEmits<Emits>()

	const statuses = [
		{ value: 'active', label: 'Activo' },
		{ value: 'inactive', label: 'Inactivo' },
		{ value: 'all', label: 'Todos los estatus' },
	]

	const sortByOptions = [
		{ value: 'all', label: 'Sin ordenar' },
		{ value: 'name', label: 'Nombre' },
		{ value: 'group', label: 'Grupo' },
		{ value: 'status', label: 'Estatus' },
		{ value: 'created_at', label: 'Fecha de creación' },
	]

	const sortDirectionOptions = [
		{ value: 'asc', label: 'Ascendente' },
		{ value: 'desc', label: 'Descendente' },
	]

	const roleOptions = computed(() => {
		return [
			{ value: 'all', label: 'Todos los roles' },
			...(props.roles?.map((role) => ({
				value: role.name,
				label: role.name,
			})) || []),
		]
	})

	// Computed para contar filtros activos
	const activeFiltersCount = computed(() => {
		let count = 0
		if (props.filters.search) count++
		if (props.filters.status && props.filters.status !== 'all') count++
		if (props.filters.role && props.filters.role !== 'all') count++
		if (props.filters.sortBy && props.filters.sortBy !== 'all') count++
		return count
	})
</script>

<template>
	<SlideOver
		:entity-type="'users'"
		title="Filtros de Usuarios Internos"
		:when="open"
		@update:close-slide-over="() => emits('close')"
		@update:save-slide-over="() => emits('close')"
		:hide-buttons="true"
	>
		<template #default>
			<div class="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 min-h-full">
				<!-- Header Section -->
				<div class="mb-8 relative">
					<!-- Decorative background -->
					<div
						class="absolute inset-0 bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl opacity-30 transform -skew-y-1"
					></div>

					<div
						class="relative bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
					>
						<div class="flex items-center justify-between mb-4">
							<div class="flex items-center gap-4">
								<div class="p-3 bg-blue-100 rounded-lg">
									<svg
										class="w-6 h-6 text-blue-600"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
										></path>
									</svg>
								</div>
								<div>
									<h3 class="text-xl font-bold text-gray-900">
										Filtros de Usuarios Internos
									</h3>
									<p class="text-sm text-gray-600 mt-1">
										Encuentra exactamente lo que buscas con nuestros filtros
										inteligentes
									</p>
								</div>
							</div>

							<!-- Botón para limpiar filtros -->
							<button
								v-if="activeFiltersCount > 0"
								@click="emits('reset')"
								class="inline-flex items-center gap-2 px-4 py-2 bg-yellow-50 hover:bg-yellow-100 text-yellow-700 text-sm font-medium rounded-lg transition-colors duration-200 border border-yellow-200"
							>
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
									></path>
								</svg>
								Limpiar ({{ activeFiltersCount }})
							</button>
						</div>

						<!-- Badges de filtros activos -->
						<div
							v-if="activeFiltersCount > 0"
							class="flex flex-wrap gap-2 pt-4 border-t border-gray-200"
						>
							<span
								class="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full"
							>
								<svg
									class="w-3.5 h-3.5"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M5 13l4 4L19 7"
									></path>
								</svg>
								{{ activeFiltersCount }} filtro{{
									activeFiltersCount !== 1 ? 's' : ''
								}}
								activo{{ activeFiltersCount !== 1 ? 's' : '' }}
							</span>
						</div>
					</div>
				</div>

				<!-- Contenido de filtros organizado en secciones -->
				<div class="space-y-6">
					<!-- Sección: Estado y Roles -->
					<div class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
						<div class="flex items-center gap-3 mb-4">
							<div class="p-2 bg-green-100 rounded-lg">
								<svg
									class="w-5 h-5 text-green-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
									></path>
								</svg>
							</div>
							<h4 class="text-lg font-semibold text-gray-900">
								Información del Usuario
							</h4>
						</div>

						<div class="grid gap-4">
							<!-- Estado -->
							<div class="space-y-2">
								<label
									class="block text-sm font-medium text-gray-700"
									for="filter-status"
								>
									<div class="flex items-center gap-2 mb-1">
										<svg
											class="w-4 h-4 text-gray-500"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
											></path>
										</svg>
										Estatus
									</div>
								</label>
								<StyledSelect
									id="filter-status"
									:model-value="filters.status"
									@update:model-value="emits('update:status', $event)"
									:options="statuses"
									class="w-full"
								/>
							</div>

							<!-- Rol -->
							<HasPermission
								:name-permission="'viewAny-internal'"
								:entity-type="'roles'"
							>
								<div class="space-y-2">
									<label
										class="block text-sm font-medium text-gray-700"
										for="filter-role"
									>
										<div class="flex items-center gap-2 mb-1">
											<svg
												class="w-4 h-4 text-gray-500"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
												></path>
											</svg>
											Rol
										</div>
									</label>
									<StyledSelect
										id="filter-role"
										:model-value="filters.role"
										@update:model-value="emits('update:role', $event)"
										:options="roleOptions"
										class="w-full"
									/>
								</div>
							</HasPermission>
						</div>
					</div>

					<!-- Sección: Ordenamiento -->
					<div class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
						<div class="flex items-center gap-3 mb-4">
							<div class="p-2 bg-purple-100 rounded-lg">
								<svg
									class="w-5 h-5 text-purple-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
									></path>
								</svg>
							</div>
							<h4 class="text-lg font-semibold text-gray-900">Ordenamiento</h4>
						</div>

						<div class="grid gap-4">
							<!-- Ordenar por -->
							<div class="space-y-2">
								<label
									class="block text-sm font-medium text-gray-700"
									for="filter-sort-by"
								>
									<div class="flex items-center gap-2 mb-1">
										<svg
											class="w-4 h-4 text-gray-500"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4"
											></path>
										</svg>
										Ordenar por
									</div>
								</label>
								<StyledSelect
									id="filter-sort-by"
									:model-value="filters.sortBy"
									@update:model-value="emits('update:sortBy', $event)"
									:options="sortByOptions"
									class="w-full"
								/>
							</div>

							<!-- Dirección (solo si hay ordenamiento) -->
							<div
								v-if="filters.sortBy && filters.sortBy !== 'all'"
								class="space-y-2"
							>
								<label
									class="block text-sm font-medium text-gray-700"
									for="filter-sort"
								>
									<div class="flex items-center gap-2 mb-1">
										<svg
											class="w-4 h-4 text-gray-500"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
											></path>
										</svg>
										Dirección
									</div>
								</label>
								<StyledSelect
									id="filter-sort"
									:model-value="filters.sort"
									@update:model-value="emits('update:sort', $event)"
									:options="sortDirectionOptions"
									class="w-full"
								/>
							</div>
						</div>
					</div>

					<!-- Información adicional -->
					<div
						class="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200"
					>
						<div class="flex items-start gap-3">
							<div class="p-2 bg-blue-100 rounded-lg flex-shrink-0">
								<svg
									class="w-5 h-5 text-blue-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
									></path>
								</svg>
							</div>
							<div>
								<h5 class="text-sm font-semibold text-gray-900 mb-1">
									💡 Consejo útil
								</h5>
								<p class="text-xs text-gray-600">
									Los filtros se aplican automáticamente. Usa el buscador en la
									parte superior para encontrar usuarios por nombre o email.
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
	</SlideOver>
</template>

<style scoped>
	/* Animaciones suaves para los filtros */
	.space-y-6 > * {
		animation: fadeInUp 0.4s ease-out forwards;
		opacity: 0;
	}

	.space-y-6 > *:nth-child(1) {
		animation-delay: 0.1s;
	}

	.space-y-6 > *:nth-child(2) {
		animation-delay: 0.2s;
	}

	.space-y-6 > *:nth-child(3) {
		animation-delay: 0.3s;
	}

	@keyframes fadeInUp {
		from {
			opacity: 0;
			transform: translateY(20px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}
</style>
